import { inject, Injectable } from '@angular/core';
import { IAuthLogin } from '../interfaces/auth-user.interface';
import { Observable, of, switchMap, tap, timer } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { LocalStorageService } from './local-storage.service';

export enum Endpoints {
  LOGIN = '/auth/loginOL',
  REFRESH = '/auth/refresh-token'
};
const REFRESH_DELAY = 0;
const TOKEN_LIMIT = 9 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);

  //#region Login auth
  public login(user: any) {
    const url = environment.auth.apiUrl + Endpoints.LOGIN;
    const body = {
      username: user.username,
      password: user.password,
      appId: environment.auth.appId
    };

    return this.http.post<IAuthLogin>(url, body).pipe(
      tap((result: IAuthLogin) => {
        // console.log('Login result: ', result);
        this.setUserLogged(result);
      })
    ) as Observable<IAuthLogin>;
  }

  private setUserLogged(loginResult: IAuthLogin) {
    if (loginResult.access_token) {
      this.localStorageService.set('user', loginResult as IAuthLogin);
    }
  }

  // public checkTokenExpiration() {
  public isTokenValid() {
    const user: IAuthLogin | null = this.localStorageService.get<IAuthLogin>('user');
    return user && new Date() < new Date(user.expires_in);
  }

  public logout(): void {
    // console.log('LOGOUT');
    this.localStorageService.remove('user');
    this.router.navigate(['/login']);
  }
  //#endregion

  //#region Refresh auth
  public setupSessionRefresh(): void {
    console.log('Setting up session refresh');
    timer(REFRESH_DELAY, TOKEN_LIMIT).pipe(
      switchMap(() => {
        const currentUrl = this.router.url;
        // Don't logout or refresh if we are in the VR route
        if (currentUrl.includes('/experience/vr')) {
          return of(null);
        }

        // Check if the user is logged in and has a refresh token
        const user: IAuthLogin | null = this.localStorageService.get<IAuthLogin>('user');
        if (user && user.refresh_token) {
          return this.refreshToken();
        } else {
          this.logout();
          return of(null);
        }
      })
    ).subscribe({
      // next: (result) => console.log('SESSION refreshed'),
      error: (error) => {
        console.error('Error refreshing session: ', error);
        const currentUrl = this.router.url;
        // Don't logout if we are in the VR route
        if (!currentUrl.includes('/experience/vr')) {
          this.logout();
        }
      }
    });
  }

  private refreshToken(): Observable<IAuthLogin> {
    const user = this.localStorageService.get<IAuthLogin>('user');
    if (!user) {
      throw new Error('User not found in application');
    }
    const { refresh_token, token_type } = user;
    const headers = new HttpHeaders({
      Authorization: `${token_type} ${refresh_token}`,
    });
    const body = { appId: environment.auth.appId };

    return this.http.post<IAuthLogin>(environment.auth.apiUrl + Endpoints.REFRESH, body, { headers })
      .pipe(
        tap({
          next: (result) => this.setUserLogged(Object.assign(user, result) as IAuthLogin),
          error: (error) => {
            console.error('Error refreshing token: ', error);
            this.logout();
          }
        })
      );
  }
  //#endregion

}
